import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessSettings as canAccessSettingsFor, roleLabel } from '../lib/roles';
import { api } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Calendar,
  Monitor,
  Settings,
  LogOut,
  Menu,
  X,
  Receipt,
  TableProperties,
  TrendingUp,
  CalendarDays,
  Building2,
  ChevronsUpDown,
  Check,
  ShoppingCart,
  KeyRound
} from 'lucide-react';
import { useCompanyBranding } from '../hooks/useCompanyLogo';
import ChangePasswordDialog from './ChangePasswordDialog';
import { useFeatures } from '../lib/features';

const Layout = ({ children }) => {
  const { user, logout, switchProperty } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { logoUrl, companyName } = useCompanyBranding();

  const isSuper = user?.role === 'super_admin';
  const [properties, setProperties] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  useEffect(() => {
    if (isSuper) {
      api.get('/properties').then(setProperties).catch(() => {});
    }
  }, [isSuper]);

  const activePropId = user?.active_property_id;
  const activeProp = properties.find(p => p.id === activePropId) || user?.property;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitch = async (propId) => {
    if (propId === activePropId) { setSwitcherOpen(false); return; }
    setSwitcherOpen(false);
    await switchProperty(propId);
  };

  const { on: featureOn, clientsLabel } = useFeatures();
  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', feature: 'dashboard', roles: ['admin', 'user', 'sales_admin', 'accounting'] },
    { to: '/advertisers', icon: Users, label: clientsLabel, roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/bookings', icon: FileText, label: 'Bookings', feature: 'bookings', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/orders', icon: ShoppingCart, label: 'Orders', feature: 'orders', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/editions', icon: Calendar, label: 'Editions', feature: 'editions', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/digital', icon: Monitor, label: 'Digital', feature: 'digital', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/accounting', icon: Receipt, label: 'Accounting', feature: 'accounting', roles: ['admin', 'user', 'sales_admin', 'accounting'] },
    { to: '/sales', icon: TrendingUp, label: 'Sales', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/planner', icon: CalendarDays, label: 'Sales Planner', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
    { to: '/ad-matrix', icon: TableProperties, label: 'Ad Matrix', feature: 'ad_matrix', roles: ['admin', 'user', 'sales_admin', 'accounting', 'sales_rep'] },
  ]
    // Modules switched off for this property disappear for everyone,
    // including the super admin (they are blocked on the server as well).
    .filter(item => !item.feature || featureOn(item.feature))
    .filter(item => isSuper || item.roles.includes(user?.role || 'user'));

  const canAccessSettings = canAccessSettingsFor(user);

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
          isActive
            ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-l-2 border-[#3B82F6]'
            : 'text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC]'
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </NavLink>
  );

  const PropertySwitcher = () => {
    if (!isSuper) return null;
    return (
      <div className="mt-3 relative" data-testid="property-switcher">
        <button
          type="button"
          onClick={() => setSwitcherOpen(o => !o)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#1E293B] text-left hover:border-[#3B82F6]/50 transition-colors"
          data-testid="property-switcher-btn"
          aria-expanded={switcherOpen}
        >
          <Building2 className="w-4 h-4 text-[#3B82F6] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-[#475569]">Property</p>
            <p className="text-sm font-medium text-[#F8FAFC] truncate" data-testid="active-property-name">
              {activeProp?.name || 'Select property'}
            </p>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-[#94A3B8] shrink-0" />
        </button>
        {switcherOpen && (
          <div
            className="absolute left-0 right-0 mt-1 z-50 bg-[#121212] border border-[#1E293B] rounded-md shadow-xl overflow-hidden"
            data-testid="property-switcher-menu"
          >
            <div className="max-h-64 overflow-y-auto py-1">
              {properties.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleSwitch(p.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#1E293B] transition-colors"
                  data-testid={`property-option-${p.id}`}
                >
                  <Check className={`w-4 h-4 ${p.id === activePropId ? 'text-[#3B82F6]' : 'text-transparent'}`} />
                  <span className="text-sm text-[#F8FAFC] truncate flex-1">{p.name}</span>
                  {p.is_primary && <span className="text-[10px] text-[#475569]">primary</span>}
                </button>
              ))}
            </div>
            <NavLink
              to="/properties"
              onClick={() => setSwitcherOpen(false)}
              className="flex items-center gap-2 px-3 py-2 border-t border-[#1E293B] text-[#3B82F6] hover:bg-[#1E293B] text-sm font-medium"
              data-testid="manage-properties-link"
            >
              <Settings className="w-4 h-4" /> Manage Properties
            </NavLink>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#121212] border-r border-[#1E293B] fixed top-0 left-0 h-screen z-30">
        {/* Logo */}
        <div className="p-6 border-b border-[#1E293B]">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName || 'Company logo'} className="h-10" />
            ) : (
              <img src="/publishos-logo.svg" alt="PublishOS" className="h-9" />
            )}
          </div>
          {companyName && (
            <p className="text-xs text-[#94A3B8] mt-2 truncate">{companyName}</p>
          )}
          <PropertySwitcher />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {isSuper && (
            <NavItem to="/properties" icon={Building2} label="Properties" />
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-[#1E293B]">
          {canAccessSettings && (
            <NavItem to="/settings" icon={Settings} label="Settings" />
          )}
          
          <div className="flex items-center gap-3 mb-3 mt-3">
            <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#F8FAFC] truncate">{user?.name}</p>
              <p className="text-xs text-[#94A3B8] truncate">{isSuper ? 'super admin' : roleLabel(user?.role)}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => setShowChangePassword(true)}
            className="w-full justify-start text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]"
            data-testid="change-password-btn"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Change Password
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-[#94A3B8] hover:text-red-400 hover:bg-red-900/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121212] border-b border-[#1E293B] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName || 'Company logo'} className="h-8" />
          ) : (
            <img src="/publishos-logo.svg" alt="PublishOS" className="h-7" />
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-[#94A3B8]"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#0A0A0A]/95 pt-16 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {isSuper && (
              <div className="pb-2 mb-2 border-b border-[#1E293B]">
                <p className="text-[10px] uppercase tracking-wide text-[#475569] px-4 mb-1">Active property</p>
                <p className="text-sm font-medium text-[#F8FAFC] px-4" data-testid="mobile-active-property">{activeProp?.name || '—'}</p>
              </div>
            )}
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
            {isSuper && (
              <NavItem to="/properties" icon={Building2} label="Properties" />
            )}
            {canAccessSettings && (
              <NavItem to="/settings" icon={Settings} label="Settings" />
            )}
            <button
              onClick={() => { setMobileMenuOpen(false); setShowChangePassword(true); }}
              className="flex items-center gap-3 px-4 py-2.5 rounded-md text-[#94A3B8] hover:bg-[#1E293B] w-full"
              data-testid="mobile-change-password-btn"
            >
              <KeyRound className="w-5 h-5" />
              <span className="font-medium">Change Password</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2.5 rounded-md text-red-400 hover:bg-red-900/20 w-full"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </nav>
        </div>
      )}

      <ChangePasswordDialog isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />

      {/* Main content */}
      <main className="flex-1 lg:ml-64 lg:p-8 p-4 pt-20 lg:pt-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
